import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { restoreSessionFailure, restoreSessionStart, restoreSessionSuccess } from './store/slices/authSlice';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import PersonalityAssessmentPage from './pages/PersonalityAssessmentPage';
import AvatarPage from './pages/AvatarPage';
import MatchDiscoveryPage from './pages/MatchDiscoveryPage';
import MatchesPage from './pages/MatchesPage';
import MessagesPage from './pages/MessagesPage';
import NotificationsPage from './pages/NotificationsPage';
import LiveMatchingTheaterPage from './pages/LiveMatchingTheaterPage';
import WebSocketTestPage from './pages/WebSocketTestPage';
import SettingsPage from './pages/SettingsPage';
import CompatibilityReportPage from './pages/CompatibilityReportPage';
import ConversationHistoryPage from './pages/ConversationHistoryPage';
import MatchConversationsPage from './pages/MatchConversationsPage';
import ProfileManagementPage from './pages/ProfileManagementPage';
import ScenarioSimulationPage from './pages/ScenarioSimulationPage';
import Wave2PrototypePage from './pages/Wave2PrototypePage';
import AvatarShareReportPage from './pages/AvatarShareReportPage';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import LoadingSpinner from './components/LoadingSpinner';

const App: React.FC = () => {
    const dispatch = useAppDispatch();
    const { isLoading, token, user } = useAppSelector(state => state.auth);

    useEffect(() => {
        if (!token || user || isLoading) {
            return;
        }

        const restoreSession = async () => {
            try {
                dispatch(restoreSessionStart());

                const response = await fetch('/api/v1/auth/me', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Session expired, please sign in again');
                }

                const currentUser = await response.json();
                dispatch(restoreSessionSuccess({ user: currentUser }));
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Failed to restore session';
                dispatch(restoreSessionFailure(message));
            }
        };

        restoreSession();
    }, [dispatch, isLoading, token, user]);

    if (isLoading) {
        return (
            <LoadingSpinner
                fullScreen
                message="Loading your profile..."
                delay={300}
            />
        );
    }

    return (
        <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/wave2-prototype" element={<Wave2PrototypePage />} />
            <Route path="/auth/*" element={<AuthPage />} />
            <Route path="/avatar/share/:userId" element={<AvatarShareReportPage />} />

            {/* Protected routes with layout */}
            <Route
                path="/dashboard/*"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <Dashboard />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/personality-assessment"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <PersonalityAssessmentPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/avatar"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <AvatarPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/discover"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <MatchDiscoveryPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/matches"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <MatchesPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/messages"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <MessagesPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/messages/:matchId"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <MessagesPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/notifications"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <NotificationsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/theater/:sessionId"
                element={
                    <ProtectedRoute>
                        <LiveMatchingTheaterPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/websocket-test"
                element={
                    <ProtectedRoute>
                        <WebSocketTestPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <ProfileManagementPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <SettingsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/compatibility/:reportId"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <CompatibilityReportPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/match/:matchId/conversations"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <MatchConversationsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/conversation/:sessionId"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <ConversationHistoryPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/scenario-simulation/:matchId"
                element={
                    <ProtectedRoute>
                        <ScenarioSimulationPage />
                    </ProtectedRoute>
                }
            />

            {/* Catch-all route for 404 */}
            <Route
                path="*"
                element={
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        flexDirection: 'column',
                        gap: '1rem'
                    }}>
                        <h1>404 - Page Not Found</h1>
                        <p>The page you're looking for doesn't exist.</p>
                    </div>
                }
            />
        </Routes>
    );
};

export default App;
